import type { Brief, CampaignActionPlan, TaskRunListItem } from "../../../shared/types";
import type { InboxItem } from "./inbox-selectors";
import { inboxItemRoute } from "./inbox-selectors";
import { taskRunJobLabel } from "./task-run-presentation";

export type WorkFeedFilter = "attention" | "all" | "complete";
export interface WorkFeedItem {
  id: string;
  title: string;
  status: string;
  reason: string;
  context?: string;
  attention: boolean;
  complete: boolean;
  updatedAt: string;
  href: string;
  actionLabel: string;
}
export type WorkspaceActionPlan = CampaignActionPlan & { campaign_title: string };

/** Saved plans are addressable work, but saving or archiving proves neither attention nor completion. */
export function workspaceActionPlanItems(plans: WorkspaceActionPlan[], brandId: string): WorkFeedItem[] {
  return plans.filter(plan => plan.brand_id === brandId).map(plan => ({
    id: `plan:${plan.id}`,
    title: plan.title,
    status: plan.status === "archived" ? "Archived plan" : "Planned",
    reason: plan.objective,
    context: plan.campaign_title,
    attention: false,
    complete: false,
    updatedAt: plan.updated_at,
    href: `/brand/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(plan.campaign_id)}/action-plans/${encodeURIComponent(plan.id)}`,
    actionLabel: "Open plan",
  }));
}
export function readWorkFeedFilter(value: string | null, fallback: WorkFeedFilter): WorkFeedFilter {
  return value === "attention" || value === "all" || value === "complete" ? value : fallback;
}
export function filterWorkFeed(items: WorkFeedItem[], filter: WorkFeedFilter): WorkFeedItem[] {
  return items.filter(item => filter === "all" || (filter === "attention" ? item.attention : item.complete));
}
export function briefWorkItem(brief: Brief, now = Date.now()): WorkFeedItem {
  const root = `/brand/${encodeURIComponent(brief.brand_id)}/campaigns/${encodeURIComponent(brief.campaign_id)}`;
  const review = brief.state === "in_review" || brief.state === "approved" || brief.state === "scheduled";
  const complete = brief.state === "published" || brief.state === "cancelled";
  // A brief created from an idea is stamped scheduled_at = its creation time; that is not a due date, and
  // calling it overdue the moment it exists was wrong (round-3 P2). Only an explicit schedule counts.
  const explicitSchedule = !!brief.scheduled_at && !(brief.created_at && Math.abs(Date.parse(brief.scheduled_at) - Date.parse(brief.created_at)) < 60_000);
  const due = brief.due_at ?? (explicitSchedule ? brief.scheduled_at : null);
  const overdue = !complete && !!due && Date.parse(due) < now;
  const attention = review || brief.state === "blocked" || overdue;
  const status = ({ in_review: "Needs review", voice_review: "Voice review", published: "Published", cancelled: "Cancelled", approved: "Approved", scheduled: "Scheduled", blocked: "Blocked" } as Record<string, string>)[brief.state] ?? brief.state.replaceAll("_", " ");
  return {
    id: `brief:${brief.id}`, title: brief.title, status, attention, complete,
    reason: brief.state === "blocked" ? brief.blocked_reason || "This action needs a blocker resolved."
      : brief.state === "in_review" ? "A draft is ready for your decision."
      : brief.state === "approved" || brief.state === "scheduled" ? "Approved content still needs a recorded publishing outcome."
      : brief.state === "published" ? "The publishing outcome is recorded."
      : brief.state === "cancelled" ? "This action was cancelled."
      : overdue ? "The action’s due date has passed."
      : brief.state === "briefed" && !explicitSchedule ? "The brief is ready. Open it and choose Draft this."
      : explicitSchedule && brief.scheduled_at ? `Planned for ${new Date(brief.scheduled_at).toLocaleDateString()}. A schedule does not confirm a draft exists.`
      : "Planned work. Open the brief to check its inputs.",
    updatedAt: brief.updated_at,
    href: review ? `/review?brandId=${encodeURIComponent(brief.brand_id)}&briefId=${encodeURIComponent(brief.id)}` : `${root}/briefs/${encodeURIComponent(brief.id)}?document=${brief.state === "published" ? "draft" : "brief"}`,
    actionLabel: review ? "Review" : brief.state === "published" ? "Read output" : "Read brief",
  };
}

/** A failed Run asks for attention for this long; after that it is history (still listed under All). */
export const FAILED_RUN_ATTENTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** One row per work object. Inbox decisions win over plan metadata. Successful
 * Run attempts stay in Activity; they never prove the associated work complete. */
export function workspaceWorkItems({ brandId, inbox, briefs, runs, now = Date.now() }: {
  brandId: string; inbox: InboxItem[]; briefs: Brief[]; runs: TaskRunListItem[]; now?: number;
}): WorkFeedItem[] {
  const items = new Map<string, WorkFeedItem>();
  for (const brief of briefs) if (brief.brand_id === brandId) items.set(`brief:${brief.id}`, briefWorkItem(brief, now));
  for (const item of inbox) {
    const id = item.gateId ? `gate:${item.gateId}` : item.briefId ? `brief:${item.briefId}` : `inbox:${item.id}`;
    items.set(id, { id, title: item.title, status: item.type === "approval" ? "Needs review" : "Blocked", reason: item.description, context: item.subtitle, attention: true, complete: false, updatedAt: item.timestamp, href: inboxItemRoute(item, brandId), actionLabel: item.actionLabel });
  }
  const latest = new Map<string, TaskRunListItem>();
  for (const run of runs) {
    if (run.brand_id !== brandId) continue;
    // Separate unspecialized attempts stay distinct; only an explicit scope or
    // work-object identity supplies evidence that a newer attempt supersedes one.
    const identity = run.brief_id ? `${run.task_slug}:brief:${run.brief_id}` : run.run_scope ? `${run.task_slug}:${run.run_scope}` : `run:${run.id}`;
    const previous = latest.get(identity);
    if (!previous || Date.parse(run.started_at) > Date.parse(previous.started_at)) latest.set(identity, run);
  }
  // Walkthrough #9: one failing action used to flood Needs attention (ThinkRun showed ~20 rows).
  // Every failure stays listed under All, but only the latest failure of one action on one
  // campaign, and only within the last week, asks for attention.
  const latestFailure = new Map<string, TaskRunListItem>();
  for (const run of latest.values()) {
    if (run.status !== "failed") continue;
    const key = `${run.task_slug}:${run.campaign_id ?? "brand"}`;
    const previous = latestFailure.get(key);
    if (!previous || Date.parse(run.started_at) > Date.parse(previous.started_at)) latestFailure.set(key, run);
  }
  for (const [identity, run] of latest) {
    if ((run.brief_id && items.has(`brief:${run.brief_id}`)) || items.has(identity) || !["failed", "running", "retrying"].includes(run.status)) continue;
    const failed = run.status === "failed";
    const superseded = failed && latestFailure.get(`${run.task_slug}:${run.campaign_id ?? "brand"}`)?.id !== run.id;
    const aged = failed && !superseded && now - Date.parse(run.started_at) > FAILED_RUN_ATTENTION_WINDOW_MS;
    items.set(identity, { id: `run:${run.id}`, title: taskRunJobLabel(run),
      status: !failed ? "Running" : superseded ? "Earlier failure" : "Failed",
      reason: !failed ? "The action is still running. Completion is not confirmed."
        : superseded ? "A newer attempt of this action also failed; start from that one."
        : aged ? "This failed more than 7 days ago. Open the details if you still need it."
        : "Open the failure details before deciding whether to retry.",
      attention: failed && !superseded && !aged, complete: false, updatedAt: run.started_at, href: `/runs/${encodeURIComponent(run.id)}`, actionLabel: failed ? "Inspect failure" : "View progress" });
  }
  return [...items.values()].sort((a, b) => Number(b.attention) - Number(a.attention) || Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}
